import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { UserPlus, Building2, User, CheckCircle, ArrowLeft } from 'lucide-react';
import api from '../services/api';

const OpenAccount = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [customerType, setCustomerType] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    document: '',
    phone: ''
  });

  // Ler parâmetro de query e pré-selecionar tipo
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'PF' || typeParam === 'PJ') {
      setCustomerType(typeParam);
      setStep(2);
    }
  }, [searchParams]);

  const handleTypeSelect = (type) => {
    setCustomerType(type);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/public/open-account', {
        ...formData,
        customer_type: customerType
      });

      setSuccess(true);
      toast({
        title: 'Solicitação enviada!',
        description: 'Sua conta será analisada em breve.',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao enviar solicitação';
      
      // Tratar mensagens específicas
      let userMessage = errorMessage;
      if (errorMessage.includes('rate limit') || errorMessage.includes('Muitas tentativas')) {
        userMessage = 'Muitas tentativas. Por favor, aguarde alguns minutos e tente novamente.';
      } else if (errorMessage.includes('já está cadastrado') || errorMessage.includes('already registered')) {
        userMessage = 'Este email já está cadastrado. Tente fazer login.';
      }
      
      toast({
        variant: 'destructive',
        title: 'Erro ao criar conta',
        description: userMessage,
        duration: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <CheckCircle className="h-8 w-8 text-success" strokeWidth={1.5} />
            </div>
            <CardTitle className="text-2xl">Solicitação Enviada!</CardTitle>
            <CardDescription className="text-base">
              Sua solicitação de abertura de conta foi recebida e está aguardando análise.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-muted/50 p-4 space-y-2">
              <p className="text-sm font-medium">Próximos passos:</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Aguarde a análise inicial</li>
                <li>Faça login com suas credenciais</li>
                <li>Complete o processo de KYC</li>
                <li>Sua conta será ativada após aprovação</li>
              </ol>
            </div>
            <Button
              className="w-full h-11 rounded-full"
              onClick={() => navigate('/login')}
              data-testid="go-to-login-button"
            >
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header simples */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-14 items-center px-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Voltar para Home</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold tracking-tight">Abrir Conta Digital</CardTitle>
            <CardDescription className="text-base">Comece sua jornada financeira conosco</CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold mb-2">Escolha o tipo de conta</h3>
                  <p className="text-sm text-muted-foreground">Selecione o tipo que melhor se adequa a você</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card
                    className="cursor-pointer transition-all hover:shadow-lg hover:scale-105 border-2 hover:border-primary"
                    onClick={() => handleTypeSelect('PF')}
                    data-testid="select-pf-button"
                  >
                    <CardContent className="pt-6 text-center space-y-4">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                        <User className="h-8 w-8 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg">Pessoa Física</h4>
                        <p className="text-sm text-muted-foreground">Conta individual para CPF</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    className="cursor-pointer transition-all hover:shadow-lg hover:scale-105 border-2 hover:border-primary"
                    onClick={() => handleTypeSelect('PJ')}
                    data-testid="select-pj-button"
                  >
                    <CardContent className="pt-6 text-center space-y-4">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
                        <Building2 className="h-8 w-8 text-accent" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg">Pessoa Jurídica</h4>
                        <p className="text-sm text-muted-foreground">Conta empresarial para CNPJ</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="text-center space-y-2">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="rounded-full"
                  >
                    Já tenho conta - Fazer login
                  </Button>
                </div>
              </div>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center gap-2 mb-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                  className="rounded-full"
                >
                  ← Voltar
                </Button>
                <span className="text-sm text-muted-foreground">
                  Tipo selecionado: <strong>{customerType === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}</strong>
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-11"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    className="h-11"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">{customerType === 'PF' ? 'Nome Completo' : 'Razão Social'}</Label>
                <Input
                  id="full_name"
                  placeholder={customerType === 'PF' ? 'João da Silva' : 'Empresa ABC Ltda'}
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="h-11"
                  data-testid="full-name-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="document">{customerType === 'PF' ? 'CPF' : 'CNPJ'}</Label>
                  <Input
                    id="document"
                    placeholder={customerType === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                    value={formData.document}
                    onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                    required
                    className="h-11"
                    data-testid="document-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    placeholder="(00) 00000-0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="h-11"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-full shadow-lg"
                disabled={loading}
                data-testid="submit-application-button"
              >
                <UserPlus className="mr-2 h-5 w-5" strokeWidth={1.5} />
                {loading ? 'Enviando...' : 'Enviar Solicitação'}
              </Button>
            </form>
          )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OpenAccount;